/**
 * PDP redesign — sticky cart bar extras.
 * Relies on theme.js for core cart bar behaviour (scroll, add to cart, configure scroll).
 */
(function () {
  'use strict';

  const LOG_PREFIX = '[pdp-rd-cart-bar]';

  const SELECTORS = {
    section: '[data-section-type="product"][data-cart-bar-enabled]',
    cartBar: '#cart-bar.pdp-rd--cart-bar',
    form: '[data-product-form]',
    mainQty: 'input[name="quantity"]',
    cartBarQty: '[data-cart-bar-qty-input]',
    cartBarAdd: '[data-add-to-cart-bar]',
    cartBarAtcLabel: '[data-cart-bar-atc-label]',
    cartBarPrice: '[data-cart-bar-price]',
    cartBarPriceSep: '[data-cart-bar-price-sep]',
    cartBarPaymentScroll: '[data-cart-bar-payment-scroll]',
    mainPrice: '[data-product-price]',
    isPreOrder: '[data-product-preorder]',
    morePaymentOptions: '.shopify-payment-button__more-options',
    shopifyPaymentButton: '.shopify-payment-button',
    productJson: '[data-product-json]',
    variantId: '[name="id"]',
  };

  const initialized = new WeakSet();

  function logWarning(error) {
    if (typeof console !== 'undefined' && typeof console.warn === 'function') {
      console.warn(LOG_PREFIX, error);
    }
  }

  function safeRun(fn) {
    try {
      return fn();
    } catch (error) {
      logWarning(error);
      return undefined;
    }
  }

  function isElement(node) {
    return node instanceof Element;
  }

  function isValidVariant(variant) {
    return Boolean(variant && typeof variant === 'object' && variant.id != null);
  }

  function getStrings() {
    return window.theme?.strings || {};
  }

  function getProductForm(section) {
    if (!isElement(section)) return null;
    return section.querySelector(SELECTORS.form);
  }

  function getVariantIdInput(section, form) {
    if (!isElement(section)) return null;

    const formId = form?.getAttribute?.('id');

    if (formId) {
      const linkedInputs = section.querySelectorAll(SELECTORS.variantId);
      for (const input of linkedInputs) {
        if (input.getAttribute('form') === formId) return input;
      }
    }

    const inputInForm = form?.querySelector?.(SELECTORS.variantId);
    if (inputInForm) return inputInForm;

    return section.querySelector(SELECTORS.variantId);
  }

  function getSelectedVariant(section) {
    if (!isElement(section)) return null;

    const productJsonEl = section.querySelector(SELECTORS.productJson);
    const form = getProductForm(section);
    const idInput = getVariantIdInput(section, form);
    const rawJson = productJsonEl?.textContent?.trim();

    if (!rawJson || !idInput?.value) return null;

    try {
      const product = JSON.parse(rawJson);
      const variantId = Number.parseInt(idInput.value, 10);

      if (!Number.isFinite(variantId) || !Array.isArray(product?.variants)) {
        return null;
      }

      return product.variants.find((item) => item?.id === variantId) || null;
    } catch (error) {
      logWarning(error);
      return null;
    }
  }

  function normalizeQuantity(value) {
    const parsed = Number.parseInt(String(value), 10);
    if (!Number.isFinite(parsed) || parsed < 1) return 1;
    return parsed;
  }

  function mirrorCartBarPrice(section) {
    if (!isElement(section)) return;

    const cartBarPrice = section.querySelector(SELECTORS.cartBarPrice);
    const form = getProductForm(section);
    const sourcePrice = form?.querySelector(SELECTORS.mainPrice);

    if (!cartBarPrice || !sourcePrice) return;

    cartBarPrice.innerHTML = sourcePrice.innerHTML;
  }

  /**
   * Only applies when the bar has a direct add button (single variant, no subscriptions).
   * Multi-variant products keep the liquid "Configure" label and scroll behaviour.
   */
  function updateCartBarAddState(section, variant) {
    if (!isElement(section) || !isValidVariant(variant)) return;

    const cartBarAddBtn = section.querySelector(SELECTORS.cartBarAdd);
    if (!cartBarAddBtn) return;

    const strings = getStrings();
    let label = strings.addToCart || 'Add to cart';

    if (section.querySelector(SELECTORS.isPreOrder)) {
      label = strings.preOrder || label;
    }

    if (!variant.available) {
      label = strings.soldOut || 'Sold out';
    }

    section.querySelectorAll(SELECTORS.cartBarAtcLabel).forEach((element) => {
      element.textContent = label;
    });

    section.querySelectorAll(SELECTORS.cartBarPriceSep).forEach((element) => {
      if (variant.available) {
        element.removeAttribute('data-hidden');
      } else {
        element.setAttribute('data-hidden', '');
      }
    });

    cartBarAddBtn.disabled = !variant.available;
  }

  function onVariantChange(section, event) {
    safeRun(() => {
      const variant = isValidVariant(event?.detail?.variant)
        ? event.detail.variant
        : getSelectedVariant(section);

      if (isValidVariant(variant)) {
        updateCartBarAddState(section, variant);
      }

      mirrorCartBarPrice(section);
    });
  }

  function syncCartBarQuantity(form, cartBar, abortSignal) {
    if (!isElement(form) || !isElement(cartBar) || !abortSignal) return;

    const mainQty = form.querySelector(SELECTORS.mainQty);
    const cartBarQty = cartBar.querySelector(SELECTORS.cartBarQty);

    if (!mainQty || !cartBarQty) return;

    const syncFromMain = () => {
      safeRun(() => {
        const value = normalizeQuantity(mainQty.value);
        cartBarQty.value = String(value);
      });
    };

    const syncToMain = () => {
      safeRun(() => {
        const value = normalizeQuantity(cartBarQty.value);
        cartBarQty.value = String(value);
        mainQty.value = String(value);
        mainQty.dispatchEvent(new Event('change', {bubbles: true}));
      });
    };

    syncFromMain();
    mainQty.addEventListener('change', syncFromMain, {signal: abortSignal});
    cartBarQty.addEventListener('change', syncToMain, {signal: abortSignal});
    cartBarQty.addEventListener('input', syncToMain, {signal: abortSignal});
  }

  function bindQtyBeforeAddToCart(form, cartBar, abortSignal) {
    if (!isElement(form) || !isElement(cartBar) || !abortSignal) return;

    cartBar.addEventListener(
      'click',
      (event) => {
        safeRun(() => {
          if (!isElement(event.target)) return;

          const addButton = event.target.closest(SELECTORS.cartBarAdd);
          if (!addButton) return;

          const cartBarQty = cartBar.querySelector(SELECTORS.cartBarQty);
          const mainQty = form.querySelector(SELECTORS.mainQty);

          if (!cartBarQty || !mainQty) return;

          const value = normalizeQuantity(cartBarQty.value);
          cartBarQty.value = String(value);
          mainQty.value = String(value);
        });
      },
      {capture: true, signal: abortSignal}
    );
  }

  function bindPaymentLink(form, cartBar, abortSignal) {
    if (!isElement(form) || !isElement(cartBar) || !abortSignal) return;

    const paymentLink = cartBar.querySelector(SELECTORS.cartBarPaymentScroll);
    if (!paymentLink) return;

    paymentLink.addEventListener(
      'click',
      (event) => {
        safeRun(() => {
          event.preventDefault();

          const clickMoreOptions = () => {
            const moreOptions = form.querySelector(SELECTORS.morePaymentOptions);
            if (moreOptions && typeof moreOptions.click === 'function') {
              moreOptions.click();
              return true;
            }
            return false;
          };

          if (clickMoreOptions()) return;

          const paymentButton = form.querySelector(SELECTORS.shopifyPaymentButton);
          if (paymentButton && typeof paymentButton.scrollIntoView === 'function') {
            paymentButton.scrollIntoView({behavior: 'smooth', block: 'center'});
            window.setTimeout(() => {
              safeRun(clickMoreOptions);
            }, 400);
          }
        });
      },
      {signal: abortSignal}
    );
  }

  function setCartBarHeight(cartBar) {
    if (!isElement(cartBar) || !cartBar.classList.contains('is-visible')) return;

    const height = cartBar.offsetHeight;
    if (!Number.isFinite(height) || height < 0) return;

    document.documentElement.style.setProperty('--cart-bar-height', `${height}px`);
  }

  function observeCartBarHeight(cartBar, abortSignal) {
    if (!isElement(cartBar) || !abortSignal || typeof MutationObserver !== 'function') {
      safeRun(() => setCartBarHeight(cartBar));
      return;
    }

    const observer = new MutationObserver(() => {
      safeRun(() => setCartBarHeight(cartBar));
    });

    observer.observe(cartBar, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });

    abortSignal.addEventListener('abort', () => observer.disconnect());
    window.addEventListener('theme:resize', () => safeRun(() => setCartBarHeight(cartBar)), {signal: abortSignal});
    safeRun(() => setCartBarHeight(cartBar));
  }

  function initSection(section) {
    if (!isElement(section) || initialized.has(section)) return;

    const cartBar = section.querySelector(SELECTORS.cartBar);
    const form = getProductForm(section);

    if (!cartBar || !form) return;

    let abortController;

    try {
      if (typeof AbortController !== 'function') {
        logWarning('AbortController is not supported; skipping cart bar extras init.');
        return;
      }

      abortController = new AbortController();
      const {signal} = abortController;

      initialized.add(section);

      section.addEventListener('theme:variant:change', (event) => onVariantChange(section, event), {signal});

      syncCartBarQuantity(form, cartBar, signal);
      bindQtyBeforeAddToCart(form, cartBar, signal);
      bindPaymentLink(form, cartBar, signal);
      observeCartBarHeight(cartBar, signal);

      const initialVariant = getSelectedVariant(section);
      if (isValidVariant(initialVariant)) {
        updateCartBarAddState(section, initialVariant);
      }

      mirrorCartBarPrice(section);

      section.addEventListener(
        'shopify:section:unload',
        () => {
          safeRun(() => abortController.abort());
          initialized.delete(section);
        },
        {once: true}
      );
    } catch (error) {
      initialized.delete(section);
      safeRun(() => abortController?.abort());
      logWarning(error);
    }
  }

  function init() {
    safeRun(() => {
      document.querySelectorAll(SELECTORS.section).forEach((section) => {
        initSection(section);
      });
    });
  }

  function onSectionLoad(event) {
    safeRun(() => {
      const target = event?.target;
      if (!isElement(target)) return;

      const section =
        (typeof target.matches === 'function' && target.matches(SELECTORS.section) ? target : null) ||
        target.querySelector(SELECTORS.section) ||
        (typeof target.closest === 'function' ? target.closest(SELECTORS.section) : null);

      if (section) initSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
})();
